import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Table, TextInput, Button, Group } from '@mantine/core';
import { useSelector } from 'react-redux';
import {
  fetchEquipmentCurrency,
  fetchEquipmentCurrencyReportXLS,
  IEquipmentCurrency,
  updateEquipmentWorkingCapital
} from '../../../libraries/equipment';
import { AppState } from '../../client-redux/reducers';
import { ILocation } from '../../../libraries/location';
import { Language } from '../../languages/languageHandler';
import { LoginState } from '../../client-redux/login/reducer';
import Header from '../../components/util/header';
import { delegationCheck, formatCurrAmount } from '../../utils/script';
import { ICurrency } from '../../../libraries/currencies';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import './styles.scss';

enum MODE {
  READ = 'read',
  EDIT = 'edit'
}

const EquipmentCurrency = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const user = useSelector<AppState, LoginState>(state => state.login);
  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = useRef(new AbortController()).current;
  const [loading, setLoading] = useState(false);
  const { mode: modeParam, eid, sid } = useParams();
  const [equipmentList, setEquipmentList] = useState<IEquipmentCurrency[]>([]);
  const [updatedValues, setUpdatedValues] = useState<Record<number, number>>({});
  const [currenciesList, setCurrenciesList] = useState<ICurrency[]>([]);
  const [selectedCid, setSelectedCid] = useState<number | null>(null);

  const filteredEquipment = useMemo(() => {
    if (!equipmentList || selectedCid === null) return [];
    return equipmentList.filter(item => item.cid === selectedCid);
  }, [equipmentList, selectedCid]);

  const [, /* totalRows */ setTotalRows] = useState<number>(0);

  const mode = useMemo(() => {
    switch (modeParam) {
      case MODE.EDIT:
      case MODE.READ:
        return modeParam as MODE;
      default:
        return MODE.READ;
    }
  }, [modeParam]);

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();
  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData) {
      loadEquipmentCurrency();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData]);

  useEffect(() => {
    loadEquipmentCurrency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid]);

  useEffect(() => {
    if (currenciesList && currenciesList.length > 0 && selectedCid === null) {
      const firstCid = currenciesList[0].cid;
      setSelectedCid(firstCid);
      if (filterBarData?.currency !== firstCid) {
        handleFilterBarOnDataChange({ ...filterBarData, currency: firstCid });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currenciesList, filterBarData]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);

    if (data.currency !== undefined) {
      setSelectedCid(Number(data.currency));
    }
  };

  const loadEquipmentCurrency = () => {
    setLoading(true);
    setEquipmentList([]);
    setTotalRows(0);

    fetchEquipmentCurrency(controller, Number(eid))
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setEquipmentList(response.data?.list || []);
          setTotalRows(response.data?.totalRows || 0);

          const uniqueCurrencies = Array.from(
            new Map(response.data.list.map(item => [item.cid, item as unknown as ICurrency])).values()
          ) as ICurrency[];

          setCurrenciesList(uniqueCurrencies);
        } else {
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .catch(err => {
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const checkForCurrencyType = (type: number) => {
    if (type === 1) {
      return lang.GLOBAL_COIN;
    } else if (type === 2) {
      return lang.GLOBAL_BILL;
    } else {
      return 'Unknown Type';
    }
  };

  const handleInputChange = (cid: number, index: number, newValue: number) => {
    setUpdatedValues(prev => ({ ...prev, cid, [index]: newValue }));
  };

  const handleSaveWC = async (cid: number) => {
    if (mode !== MODE.EDIT) return;

    const payload: IEquipmentCurrency = {
      cid,
      denominations: Object.entries(updatedValues)
        .filter(([index, wc]) => index !== 'null' && !isNaN(Number(index)) && wc !== undefined)
        .map(([index, wc]) => ({
          index: parseInt(index, 10),
          wc
        }))
    };

    try {
      const response = await updateEquipmentWorkingCapital(controller, {
        eid: Number(eid),
        currency: payload
      });

      if (isZarphSuccessResponse(response)) {
        loadEquipmentCurrency();
      } else {
        handleApiError(response, lang.EQUIPMENT_UPDATE_WC_ERROR);
      }
    } catch (err: any) {
      handleApiError(err, lang.EQUIPMENT_UPDATE_WC_ERROR);
    }
  };

  const handleClearWC = () => {
    if (!equipmentList) return;

    const clearedValues = equipmentList.reduce(
      (acc, equipmentCurrency) => {
        equipmentCurrency.denominations?.forEach(denomination => {
          acc[denomination.index] = 0;
        });
        return acc;
      },
      {} as Record<number, number>
    );
    setUpdatedValues(clearedValues);
  };

  const handleFillWithExistRecycler = () => {
    if (!equipmentList) return;

    const updated = equipmentList.reduce(
      (acc, equipmentCurrency) => {
        equipmentCurrency.denominations?.forEach(denomination => {
          if (denomination.existRecycler !== undefined) {
            acc[denomination.index] = denomination.existRecycler;
          }
        });
        return acc;
      },
      {} as Record<number, number>
    );

    setUpdatedValues(updated);
  };

  const getXLSReport = async () => {
    if (!filterBarData) return;

    try {
      const response = await fetchEquipmentCurrencyReportXLS(controller, Number(eid));
      if (!isZarphSuccessResponse(response)) {
        handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
      }
    } catch (err: any) {
      handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
    }
  };

  return (
    <div className="equipment-currency">
      <Header pageTitle={`EID: ${eid} - ${delegationCheck(Number(sid), locations) || 'undefined'}`} />
      <FilterBar
        onSearch={loadEquipmentCurrency}
        onDataChange={handleFilterBarOnDataChange}
        currenciesList={currenciesList}
        fieldsUsage={{ currency: true }}
        xlsReport={getXLSReport}
      />

      <Table.ScrollContainer minWidth={500}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_INDEX}</Table.Th>
              <Table.Th>{lang.GLOBAL_VALUE}</Table.Th>
              <Table.Th>{lang.GLOBAL_TYPE}</Table.Th>
              <Table.Th>{lang.GLOBAL_WORKING_CAPITAL}</Table.Th>

              {user.permissions.hasFinancialPermissions() && (
                <>
                  <Table.Th>{lang.EQUIPMENT_EXISTENCE_RECYCLER}</Table.Th>
                  <Table.Th>{lang.EQUIPMENT_WC_SURPLUS}</Table.Th>
                  <Table.Th>{lang.EQUIPMENT_WC_LACKING}</Table.Th>
                  <Table.Th>{lang.EQUIPMENT_EXISTENCE_SAFE}</Table.Th>
                  <Table.Th>{lang.EQUIPMENT_TOTAL_EXISTENCE}</Table.Th>
                </>
              )}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading && !filteredEquipment ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : !equipmentList.length ? (
              <Table.Tr>
                <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              filteredEquipment?.map((equipmentCurrency, equipmentIndex) =>
                equipmentCurrency.denominations?.map((denomination, denominationIndex) => {
                  const matchingCurrency = currencies.find(c => c.cid === equipmentCurrency.cid);
                  return (
                    <Table.Tr key={`${equipmentIndex}-${denominationIndex}`}>
                      <Table.Td>{denomination.index}</Table.Td>
                      <Table.Td>
                        {matchingCurrency ? formatCurrAmount(denomination.value || 0, matchingCurrency) : 'N/A'}
                      </Table.Td>
                      <Table.Td>{checkForCurrencyType(denomination.type || 1)}</Table.Td>
                      <Table.Td className="input">
                        {mode === MODE.EDIT ? (
                          <TextInput
                            value={updatedValues[denomination.index] ?? denomination.wc}
                            onChange={e =>
                              handleInputChange(
                                equipmentCurrency.cid,
                                denomination.index,
                                Number(e.currentTarget.value)
                              )
                            }
                          />
                        ) : (
                          denomination.wc
                        )}
                      </Table.Td>

                      {user.permissions.hasFinancialPermissions() && (
                        <>
                          <Table.Td>{denomination.existRecycler}</Table.Td>
                          <Table.Td>{denomination.wcSurplus}</Table.Td>
                          <Table.Td>{denomination.wcLacking}</Table.Td>
                          <Table.Td>{denomination.existSafe}</Table.Td>
                          <Table.Td>{denomination.existTotal}</Table.Td>
                        </>
                      )}
                    </Table.Tr>
                  );
                })
              )
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {/* Buttons */}
      <Group className="edit-buttons" style={{ marginTop: '2vh' }}>
        {mode === MODE.EDIT && (
          <>
            <Button radius="md" color="#e4ae00" onClick={handleFillWithExistRecycler}>
              {lang.EQUIPMENT_FILL_EXISTENCE_WORKING_CAPITAL}
            </Button>
            <Button radius="md" color="#e4ae00" onClick={handleClearWC}>
              {lang.EQUIPMENT_CLEAR_WORKING_CAPITAL}
            </Button>
          </>
        )}
      </Group>
      {/* Buttons */}
      <Group style={{ marginTop: '2vh' }}>
        {mode === MODE.EDIT && (
          <>
            <Button radius="md" className="save-button" onClick={() => handleSaveWC(selectedCid || 0)}>
              {lang.GLOBAL_SAVE_BUTTON}
            </Button>
          </>
        )}
      </Group>
    </div>
  );
};

export default EquipmentCurrency;
